import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const GooglePlayPayments = ({ user, subscription, paymentHistory, userStatus, onClose, onSubscriptionUpdate }) => {
  // Funkcja wykrywania domeny
  const getCurrentDomain = () => {
    const hostname = window.location.hostname;
    if (hostname === 'losuje-play.web.app' || hostname === 'www.losuje-play.web.app') {
      return 'losuje-play';
    }
    return 'other';
  };
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [localCancelledStatus, setLocalCancelledStatus] = useState(false);

  const normalizePlanId = (planValue) => {
    if (!planValue) return null;
    if (planValue === 'premium') return 'premium_monthly';
    return planValue;
  };

  const currentStatus = localCancelledStatus
    ? 'cancelled'
    : subscription?.status || userStatus?.subscription?.subscription_status;
  const currentPlan = normalizePlanId(subscription?.plan || userStatus?.subscription?.subscription_plan);
  const statusIsTrial = currentStatus === 'trial';
  const statusIsCancelled = currentStatus === 'cancelled';
  const statusIsActive = currentStatus === 'active';

  const plans = [
    {
      id: 'premium_monthly',
      name: 'Premium Miesięczny',
      price: '9.99 zł',
      period: 'miesiąc',
      features: [
        '🚀 AI Generator Ultra Pro',
        '🎵 Analizator Harmoniczny',
        '🎲 Generator Schonheim',
        '✨ System Talizmanów',
        '📈 Zaawansowane statystyki',
        '🎰 Wszystkie gry lotto'
      ]
    },
    {
      id: 'premium_yearly',
      name: 'Premium Roczny',
      price: '59.90 zł',
      period: 'rok',
      savings: '59.88 zł',
      features: [
        '🚀 AI Generator Ultra Pro',
        '🎵 Analizator Harmoniczny',
        '🎲 Generator Schonheim',
        '✨ System Talizmanów',
        '📈 Zaawansowane statystyki',
        '🎰 Wszystkie gry lotto',
        '💎 6 miesięcy gratis!'
      ]
    }
  ];

  const handlePurchase = async (planId) => {
    setLoading(true);
    setMessage('');

    try {
      console.log('🛒 Próba zakupu planu:', planId);
      
      // Sprawdź czy użytkownik już ma aktywną subskrypcję
      if (subscription && subscription.status === 'active') {
        setMessage('⚠️ Masz już aktywną subskrypcję!');
        setLoading(false);
        return;
      }
      
      // Sprawdź czy userStatus wskazuje na aktywną subskrypcję
      if (userStatus?.subscription?.subscription_status === 'active') {
        setMessage('⚠️ Masz już aktywną subskrypcję!');
        setLoading(false);
        return;
      }
      
      // Symulacja zakupu Google Play
      const purchaseToken = 'simulated_' + Date.now();
      const orderId = 'order_' + Date.now();
      
            // Weryfikacja zakupu na backend - Functions dostępne na losuje-play.web.app
      const backendUrl = process.env.NODE_ENV === 'production' 
        ? '/api/google-play/verify-purchase'
        : `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/google-play/verify-purchase`;
      
      console.log('🔍 Wysyłam żądanie weryfikacji do:', backendUrl);
      console.log('🔍 Current domain:', window.location.hostname);
      console.log('🔍 User UID:', user?.uid);
      
      const response = await fetch(backendUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user?.uid,
          purchaseToken,
          productId: planId,
          orderId
        })
      });
      
      console.log('🔍 Response status:', response.status);
      console.log('🔍 Response ok:', response.ok);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Backend response error:', response.status, errorText);
        throw new Error(`Backend error: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      console.log('🔍 Backend result:', result);
      
      if (result.success) {
        setMessage('✅ Zakup udany! Twoja subskrypcja została aktywowana.');
        
        // Endpoint /api/google-play/verify-purchase już aktualizuje subscriptions przez updateUserSubscription
        // Nie potrzebujemy dodatkowego wywołania sync-firebase
        
        // Wymuś odświeżenie danych z backendu
        console.log('🔄 Wymuszam odświeżenie danych po zakupie...');
        
        // Wywołaj callback do odświeżenia danych (tylko raz)
        if (onSubscriptionUpdate) {
          console.log('🔄 Wywołuję onSubscriptionUpdate callback...');
          
          // Określ plan i daty na podstawie planId
          const isMonthly = planId === 'premium_monthly' || planId === 'premium';
          const daysToAdd = isMonthly ? 30 : 365;
          const normalizedPlan = isMonthly ? 'premium_monthly' : 'premium_yearly';
          
          const subscriptionData = {
            status: 'active',
            subscription_status: 'active',
            plan: normalizedPlan,
            subscription_plan: normalizedPlan,
            startDate: new Date().toISOString(),
            subscription_start_date: new Date().toISOString(), // Dodaj to pole dla kompatybilności
            endDate: new Date(Date.now() + daysToAdd * 24 * 60 * 60 * 1000).toISOString(),
            subscription_end_date: new Date(Date.now() + daysToAdd * 24 * 60 * 60 * 1000).toISOString(),
            is_blocked: false // Dodaj to pole dla kompatybilności
          };
          
          console.log('🔍 Subscription data to update:', subscriptionData);
          await onSubscriptionUpdate(subscriptionData);
          console.log('✅ onSubscriptionUpdate completed');
        } else {
          console.log('⚠️ onSubscriptionUpdate function not provided');
        }
        
        // Dodatkowe odświeżenie przez URL jako backup
        setTimeout(() => {
          const url = new URL(window.location);
          url.searchParams.set('refresh', Date.now());
          window.history.pushState({}, '', url.toString());
          
          // Wymuś przeładowanie komponentu
          window.dispatchEvent(new PopStateEvent('popstate'));
        }, 2000); // Zwiększono timeout aby dać czas na synchronizację
      } else {
        throw new Error(result.error || 'Błąd weryfikacji zakupu');
      }
    } catch (error) {
      console.error('❌ Błąd zakupu:', error);
      setMessage('❌ Błąd podczas zakupu. Spróbuj ponownie.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Czy na pewno chcesz anulować subskrypcję? Subskrypcja będzie aktywna do końca opłaconego okresu.')) {
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      console.log('❌ Anulowanie subskrypcji dla użytkownika:', user?.uid);
      
      // Wywołaj backend do anulowania subskrypcji - używamy domeny PWA
      const backendUrl = process.env.NODE_ENV === 'production' 
        ? '/api/subscription/cancel'
        : `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/subscription/cancel`;
      
      console.log('🔍 Wysyłam żądanie anulowania do:', backendUrl);
      
      const response = await fetch(backendUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user?.uid
        })
      });
      
      console.log('🔍 Response status:', response.status);
      console.log('🔍 Response ok:', response.ok);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Backend response error:', response.status, errorText);
        throw new Error(`Backend error: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      console.log('🔍 Backend result:', result);
      
      if (result.success) {
        setMessage('✅ Subskrypcja została anulowana. Pozostanie aktywna do końca opłaconego okresu.');
        
        // NATYCHMIASTOWA AKTUALIZACJA UI - ustaw lokalny stan na anulowany
        console.log('🔄 Ustawiam lokalny status na cancelled...');
        setLocalCancelledStatus(true);
        
        // KRYTYCZNE: Aktualizuj lokalny stan natychmiast - BEZ ODŚWIEŻANIA STRONY
        console.log('🔄 Aktualizuję lokalny stan na cancelled...');
        
        // Aktualizuj subskrypcję jako anulowaną
        if (onSubscriptionUpdate) {
          console.log('🔄 Wywołuję onSubscriptionUpdate callback dla anulowania...');
          const cancelledSubscription = {
            status: 'cancelled',
            plan: subscription?.plan || userStatus?.subscription?.subscription_plan,
            startDate: subscription?.startDate || userStatus?.subscription?.subscription_start_date,
            endDate: subscription?.endDate || userStatus?.subscription?.subscription_end_date,
            cancelledAt: new Date().toISOString(),
            subscription_status: 'cancelled' // Dodaj to pole dla kompatybilności
          };
          
          await onSubscriptionUpdate(cancelledSubscription);
          console.log('✅ onSubscriptionUpdate callback zakończony');
        }
      } else {
        throw new Error(result.error || 'Błąd anulowania subskrypcji');
      }
    } catch (error) {
      console.error('❌ Błąd anulowania:', error);
      setMessage('❌ Błąd podczas anulowania subskrypcji. Spróbuj ponownie.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return 'Brak danych';
    const date = typeof dateValue?.toDate === 'function' ? dateValue.toDate() : new Date(dateValue);
    if (Number.isNaN(date.getTime())) {
      return 'Brak danych';
    }
    return date.toLocaleDateString('pl-PL');
  };

  return (
    <div style={{ width: '100%' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px',
        paddingBottom: '20px',
        borderBottom: '1px solid #eee'
      }}>
        <h2 style={{ margin: 0, color: '#333', fontSize: '24px' }}>
          💳 Płatności i Subskrypcja
        </h2>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#666',
            padding: '5px'
          }}
        >
          ✕
        </button>
      </div>

      {/* Message */}
      {message && (
        <div style={{
          padding: '15px',
          marginBottom: '20px',
          borderRadius: '8px',
          backgroundColor: message.includes('✅') ? '#d4edda' : '#f8d7da',
          color: message.includes('✅') ? '#155724' : '#721c24',
          border: `1px solid ${message.includes('✅') ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          {message}
        </div>
      )}

      {/* Trial Period Info */}
      {getCurrentDomain() === 'losuje-play' && !subscription && (
        <div style={{
          background: '#fff3cd',
          padding: '20px',
          borderRadius: '12px',
          marginBottom: '30px',
          border: '1px solid #ffeaa7'
        }}>
          <h3 style={{ margin: '0 0 15px 0', color: '#856404' }}>
            ⏰ Okres Darmowy
          </h3>
          <div style={{ display: 'grid', gap: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 'bold' }}>Status:</span>
              <span style={{ color: '#856404', fontWeight: 'bold' }}>
                Aktywny
              </span>
            </div>
            {getCurrentDomain() === 'losuje-play' && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Data rejestracji:</span>
                <span>{userStatus?.created_at ? formatDate(userStatus.created_at) : 'Brak danych'}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Okres darmowy:</span>
              <span>3 dni</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Pozostało dni:</span>
              <span style={{ fontWeight: 'bold', color: '#856404' }}>
                {(() => {
                  if (userStatus?.trial_end_date) {
                    const now = new Date();
                    const trialEnd = new Date(userStatus.trial_end_date);
                    const daysLeft = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
                    return daysLeft > 0 ? `${daysLeft} dni` : 'Wygasł';
                  }
                  return '3 dni';
                })()}
              </span>
            </div>
          </div>
          <div style={{ 
            marginTop: '15px', 
            padding: '10px', 
            background: 'rgba(133, 100, 4, 0.1)', 
            borderRadius: '8px',
            fontSize: '14px',
            color: '#856404'
          }}>
            💡 Po wygaśnięciu okresu darmowego dostęp do funkcji premium zostanie ograniczony.
          </div>
        </div>
      )}

      {/* Current Subscription */}
      {(subscription || currentStatus === 'active' || statusIsCancelled || statusIsTrial) && (
        <div style={{
          background: statusIsTrial ? '#e3f2fd' : statusIsCancelled ? '#fff3cd' : '#d4edda',
          padding: '20px',
          borderRadius: '12px',
          marginBottom: '30px',
          border: statusIsTrial ? '2px solid #1e88e5' : statusIsCancelled ? '2px solid #ffc107' : '2px solid #28a745'
        }}>
          <h3 style={{ 
            margin: '0 0 15px 0', 
            color: statusIsTrial ? '#1565c0' : statusIsCancelled ? '#856404' : '#155724', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '10px' 
          }}>
            {statusIsTrial ? '🎁 Darmowy okres testowy' : statusIsCancelled ? '⚠️ Subskrypcja Anulowana' : '✅ Twoja Subskrypcja Premium'}
          </h3>
          <div style={{ display: 'grid', gap: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 'bold' }}>Status:</span>
              <span style={{ 
                color: statusIsTrial ? '#1565c0' : statusIsCancelled ? '#856404' : '#28a745',
                fontWeight: 'bold',
                background: statusIsTrial ? 'rgba(21, 101, 192, 0.15)' : statusIsCancelled ? '#ffeaa7' : '#c3e6cb',
                padding: '4px 8px',
                borderRadius: '4px'
              }}>
                {statusIsTrial ? '🎁 OKRES TESTOWY' : statusIsCancelled ? '🟡 ANULOWANA' : '🟢 AKTYWNA'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 'bold' }}>Plan:</span>
              <span style={{ fontWeight: 'bold', color: '#155724' }}>
                {statusIsTrial
                  ? '🎁 Darmowy plan (7 dni)'
                  : currentPlan === 'premium_monthly'
                    ? '💎 Premium Miesięczny'
                    : '👑 Premium Roczny'}
              </span>
            </div>
            
            {/* Daty subskrypcji */}
            {(subscription?.startDate || userStatus?.subscription?.subscription_start_date) && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Data rozpoczęcia:</span>
                <span>{formatDate(subscription?.startDate || userStatus?.subscription?.subscription_start_date)}</span>
              </div>
            )}
            
            {(subscription?.endDate || userStatus?.subscription?.subscription_end_date) && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Data zakończenia:</span>
                <span>{formatDate(subscription?.endDate || userStatus?.subscription?.subscription_end_date)}</span>
              </div>
            )}
            
            {/* Dni do odnowienia */}
            {(subscription?.endDate || userStatus?.subscription?.subscription_end_date) && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Dni do odnowienia:</span>
                <span style={{ fontWeight: 'bold', color: '#28a745' }}>
                  {(() => {
                    const now = new Date();
                    const endDate = new Date(subscription?.endDate || userStatus?.subscription?.subscription_end_date);
                    const daysLeft = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
                    return `${Math.max(0, daysLeft)} dni`;
                  })()}
                </span>
              </div>
            )}
          </div>
          
          {/* Przycisk anulowania - tylko dla płatnych subskrypcji */}
          {(statusIsActive && !localCancelledStatus) && (
            <>
              <button
                onClick={handleCancelSubscription}
                disabled={loading}
                style={{
                  background: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 20px',
                  marginTop: '20px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1,
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                {loading ? '⏳ Anulowanie...' : '❌ Anuluj Subskrypcję'}
              </button>
              
              {/* Informacja o anulowaniu */}
              <div style={{ 
                marginTop: '10px', 
                fontSize: '12px', 
                color: '#721c24',
                background: 'rgba(220, 53, 69, 0.1)',
                padding: '8px',
                borderRadius: '4px'
              }}>
                ⚠️ Po anulowaniu subskrypcja będzie aktywna do końca opłaconego okresu
              </div>
            </>
          )}
          
          {/* Informacja dla anulowanych subskrypcji */}
          {(statusIsTrial) && (
            <div style={{ 
              marginTop: '20px', 
              fontSize: '14px', 
              color: '#1565c0',
              background: 'rgba(33, 150, 243, 0.12)',
              padding: '12px',
              borderRadius: '8px',
              border: '1px solid rgba(33, 150, 243, 0.35)'
            }}>
              🎁 <strong>Masz aktywny okres testowy</strong><br/>
              Wszystkie funkcje Premium są odblokowane przez 7 dni.
              {userStatus?.subscription?.trial_end_date && (
                <>
                  <br/>
                  ⏰ <strong>Dostęp do:</strong> {formatDate(userStatus.subscription.trial_end_date)}
                </>
              )}
            </div>
          )}

          {(statusIsCancelled || localCancelledStatus) && (
            <div style={{ 
              marginTop: '20px', 
              fontSize: '14px', 
              color: '#856404',
              background: 'rgba(255, 193, 7, 0.1)',
              padding: '12px',
              borderRadius: '8px',
              border: '1px solid #ffc107'
            }}>
              📅 <strong>Subskrypcja anulowana</strong><br/>
              Twoja subskrypcja pozostanie aktywna do końca opłaconego okresu.
              {(subscription?.endDate || userStatus?.subscription?.subscription_end_date) && (
                <>
                  <br/>
                  ⏰ <strong>Dostęp do:</strong> {formatDate(subscription?.endDate || userStatus?.subscription?.subscription_end_date)}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Available Plans */}
      <div style={{ marginBottom: '30px' }}>
        <h3 style={{ margin: '0 0 20px 0', color: '#333' }}>
          🎯 Dostępne Plany
        </h3>
        
        <div style={{ display: 'grid', gap: '20px' }}>
          {plans.map((plan) => {
            const normalizedActivePlan = normalizePlanId(userStatus?.subscription?.subscription_plan);
            const isCurrentPlan = currentPlan && currentPlan === plan.id;
            const hasActiveSubscription = statusIsActive && Boolean(normalizedActivePlan);
            
            return (
              <div key={plan.id} style={{
                border: isCurrentPlan && statusIsActive ? '3px solid #28a745' : '2px solid #e9ecef',
                borderRadius: '12px',
                padding: '25px',
                position: 'relative',
                transition: 'all 0.3s ease',
                cursor: hasActiveSubscription ? 'not-allowed' : 'pointer',
                background: isCurrentPlan && statusIsActive ? '#f8fff9' : 'white',
                opacity: hasActiveSubscription && !isCurrentPlan ? 0.6 : 1,
                ':hover': {
                  borderColor: isCurrentPlan && statusIsActive ? '#28a745' : '#007bff',
                  transform: hasActiveSubscription ? 'none' : 'translateY(-2px)'
                }
              }}>
                {/* Oznaczenie aktywnego planu */}
                {isCurrentPlan && statusIsActive && (
                  <div style={{
                    position: 'absolute',
                    top: '-10px',
                    left: '20px',
                    background: '#28a745',
                    color: 'white',
                    padding: '5px 15px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}>
                    ✅ AKTYWNY PLAN
                  </div>
                )}
                
                {/* Oszczędności */}
                {plan.savings && (
                  <div style={{
                    position: 'absolute',
                    top: '-10px',
                    right: '20px',
                    background: '#28a745',
                    color: 'white',
                    padding: '5px 15px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}>
                    Oszczędź {plan.savings}
                  </div>
                )}
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h4 style={{ margin: 0, fontSize: '20px', color: '#333' }}>
                    {plan.name}
                  </h4>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#007bff' }}>
                      {plan.price}
                    </div>
                    <div style={{ fontSize: '14px', color: '#666' }}>
                      na {plan.period}
                    </div>
                  </div>
                </div>
                
                <ul style={{ 
                  listStyle: 'none', 
                  padding: 0, 
                  margin: '0 0 20px 0',
                  display: 'grid',
                  gap: '8px'
                }}>
                  {plan.features.map((feature, index) => (
                    <li key={index} style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      fontSize: '14px',
                      color: '#555'
                    }}>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <button
                  onClick={() => handlePurchase(plan.id)}
                  disabled={loading || hasActiveSubscription}
                  style={{
                    width: '100%',
                    background: isCurrentPlan && statusIsActive
                      ? '#28a745'
                      : hasActiveSubscription 
                        ? '#6c757d' 
                        : 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: hasActiveSubscription ? 'not-allowed' : 'pointer',
                    opacity: loading || (hasActiveSubscription && !isCurrentPlan) ? 0.6 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  {loading ? '⏳ Przetwarzanie...' : 
                   (isCurrentPlan && statusIsActive) ? '✅ AKTYWNY PLAN' :
                   hasActiveSubscription ? '🔒 Subskrypcja Aktywna' : 
                   `🛒 Wybierz ${plan.name}`}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Payment History */}
      {paymentHistory && paymentHistory.length > 0 && (
        <div>
          <h3 style={{ margin: '0 0 20px 0', color: '#333' }}>
            📊 Historia Płatności
          </h3>
          
          <div style={{
            background: '#f8f9fa',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            {paymentHistory.map((payment, index) => (
              <div key={index} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '15px 20px',
                borderBottom: index < paymentHistory.length - 1 ? '1px solid #e9ecef' : 'none'
              }}>
                <div>
                  <div style={{ fontWeight: 'bold', color: '#333' }}>
                    {normalizePlanId(payment.plan) === 'premium_monthly' ? 'Premium Miesięczny' : 'Premium Roczny'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {formatDate(payment.date)}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 'bold', color: '#28a745' }}>
                    {payment.amount}
                  </div>
                  <div style={{ 
                    fontSize: '12px', 
                    color: payment.status === 'completed' ? '#28a745' : '#dc3545'
                  }}>
                    {payment.status === 'completed' ? 'Zapłacone' : 'Błąd'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info */}
      <div style={{
        background: '#e7f3ff',
        padding: '15px',
        borderRadius: '8px',
        marginTop: '20px',
        border: '1px solid #b3d9ff'
      }}>
        <h4 style={{ margin: '0 0 10px 0', color: '#0056b3' }}>
          ℹ️ Informacje
        </h4>
        <ul style={{ 
          margin: 0, 
          paddingLeft: '20px', 
          color: '#0056b3',
          fontSize: '14px'
        }}>
          <li>Płatności są przetwarzane przez Google Play Store</li>
          <li>Subskrypcja automatycznie się odnawia</li>
          <li>Możesz anulować subskrypcję w dowolnym momencie</li>
          <li>Wszystkie funkcje premium są dostępne natychmiast po zakupie</li>
        </ul>
      </div>
    </div>
  );
};

export default GooglePlayPayments;
